import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import api from '../utils/api';
import PublicTenantLandingPage from '../pages/PublicTenantLandingPage';
import PublicTenantPage from '../pages/PublicTenantPage';
import RegionSelector from './RegionSelector';
import TenantCustomCode from './TenantCustomCode';

// Configuration
const BASE_DOMAIN = 'nexuscms.ai';
// Reserved subdomains that should NOT be treated as tenant slugs
// Includes regional prefixes (eu, apac, au, us) which are deployment regions
const RESERVED_SUBDOMAINS = ['www', 'app', 'admin', 'api', 'mail', 'email', 'eu', 'apac', 'ap', 'au', 'us'];

// Domains that should bypass subdomain routing (dev/preview environments)
const BYPASS_DOMAINS = ['emergentagent.com', 'localhost'];

/**
 * Check if current hostname is the root domain (nexuscms.ai or www.nexuscms.ai)
 * Should show region selector
 */
const isRootDomain = () => {
  const hostname = window.location.hostname;
  return hostname === BASE_DOMAIN || hostname === `www.${BASE_DOMAIN}`;
};

/**
 * Check if current hostname should bypass subdomain routing
 */
const shouldBypassRouting = () => {
  const hostname = window.location.hostname;
  return BYPASS_DOMAINS.some(domain => hostname.includes(domain)) || /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
};

/**
 * Check if the current hostname is a custom domain (not nexuscms.ai)
 * Returns the hostname if it's a custom domain, null otherwise
 */
const extractCustomDomain = () => {
  const hostname = window.location.hostname;
  
  // Skip for localhost, IP addresses, or bypass domains
  if (shouldBypassRouting()) {
    return null;
  }
  
  // If it's nexuscms.ai or a subdomain of it, it's not a custom domain
  if (hostname === BASE_DOMAIN || hostname.endsWith(`.${BASE_DOMAIN}`)) {
    return null;
  }
  
  // Strip www. prefix if present
  const cleanHostname = hostname.replace(/^www\./, '');
  
  return cleanHostname;
};

/**
 * Extract subdomain from current hostname
 * Examples:
 *   schoolname.nexuscms.ai -> schoolname
 *   schoolname.us.nexuscms.ai -> schoolname (multi-level with regional prefix)
 *   schoolname.eu.nexuscms.ai -> schoolname (multi-level with regional prefix)
 *   www.nexuscms.ai -> null
 *   nexuscms.ai -> null
 *   us.nexuscms.ai -> null (regional domain only)
 *   localhost:3000 -> null
 */
const extractSubdomain = () => {
  const hostname = window.location.hostname;
  
  // Skip for localhost, IP addresses, or bypass domains
  if (shouldBypassRouting()) {
    return null;
  }
  
  // Check if it's our base domain
  if (!hostname.endsWith(`.${BASE_DOMAIN}`)) {
    // Could be a custom domain - handled separately
    return null;
  }
  
  // Extract subdomain part (everything before .nexuscms.ai)
  const subdomainPart = hostname.slice(0, -(BASE_DOMAIN.length + 1));
  
  // Skip if empty
  if (!subdomainPart) {
    return null;
  }
  
  // Handle multi-level subdomains (e.g., tenant.us.nexuscms.ai)
  // Split by dots and check for regional prefixes
  const parts = subdomainPart.split('.');
  
  // If single part, check if it's reserved
  if (parts.length === 1) {
    if (RESERVED_SUBDOMAINS.includes(parts[0].toLowerCase())) {
      return null;
    }
    return parts[0].toLowerCase();
  }
  
  // If multi-level (e.g., "dwarf-high.us"), extract the tenant part
  // The last part should be a regional prefix (us, eu, au, ap, etc.)
  const lastPart = parts[parts.length - 1].toLowerCase();
  
  if (RESERVED_SUBDOMAINS.includes(lastPart)) {
    // Remove the regional prefix and return the tenant subdomain
    const tenantSubdomain = parts.slice(0, -1).join('.').toLowerCase();
    
    // Make sure the tenant part isn't also reserved
    if (!tenantSubdomain || RESERVED_SUBDOMAINS.includes(tenantSubdomain)) {
      return null;
    }
    
    return tenantSubdomain;
  }
  
  // No regional prefix found - return the full subdomain
  return subdomainPart.toLowerCase();
};

/**
 * Extract page slug from URL path
 * Supports hierarchical paths like /about/team
 * Examples:
 *   /about-us -> about-us
 *   /about/team -> about/team
 *   /services/web/design -> services/web/design
 *   / -> null (landing page)
 *   /login -> null (reserved route)
 */
const extractPageSlug = () => {
  const path = window.location.pathname;
  
  // Reserved routes that should not be treated as page slugs
  const reservedRoutes = [
    '/login', '/register', '/dashboard', '/pages', '/templates', 
    '/newsletters', '/media', '/team', '/settings', '/users',
    '/articles', '/article', '/forms', '/navigation', '/redirects', '/api', '/site',
    '/reseller', '/organization', '/audit', '/payment', '/forgot-password'
  ];
  
  // Check if path starts with any reserved route
  if (reservedRoutes.some(route => path === route || path.startsWith(route + '/'))) {
    return null;
  }
  
  // Remove leading slash and return the full path (for hierarchical URLs)
  const fullPath = path.replace(/^\//, '').replace(/\/$/, '');
  if (!fullPath) {
    return null; // Root path - show landing page
  }
  
  return fullPath;
};

/**
 * SubdomainRouter - Detects subdomain/custom domain and renders tenant site or main app
 * 
 * Routing Priority:
 * 1. `/` (root) → Try to load Page Builder page with slug "home" first
 * 2. If no "home" page exists, fall back to Landing Page (if enabled)
 * 3. `/home` → Shows Landing Page (for tenants who want simple one-page site)
 * 4. `/other-slug` → Shows the corresponding Page Builder page
 * 
 * Also supports custom domains (e.g., schooledge.ai)
 */
const SubdomainRouter = ({ children }) => {
  const [subdomain, setSubdomain] = useState(null);
  const [customDomain, setCustomDomain] = useState(null);
  const [siteSlug, setSiteSlug] = useState(null);  // Store site_slug for API calls
  const [pageSlug, setPageSlug] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [showRegionSelector, setShowRegionSelector] = useState(false);

  useEffect(() => {
    const checkSubdomainOrDomain = async () => {
      console.log('[SubdomainRouter] Starting check. Hostname:', window.location.hostname, 'Path:', window.location.pathname);
      
      // First check if we're on the root domain (nexuscms.ai or www.nexuscms.ai)
      // Should show region selector
      if (isRootDomain()) {
        setShowRegionSelector(true);
        setLoading(false);
        return;
      }
      
      // Then check for custom domain
      const detectedCustomDomain = extractCustomDomain();
      console.log('[SubdomainRouter] Detected custom domain:', detectedCustomDomain);
      
      if (detectedCustomDomain) {
        setCustomDomain(detectedCustomDomain);
        const detectedPageSlug = extractPageSlug();
        setPageSlug(detectedPageSlug);
        
        try {
          // Fetch tenant by custom domain
          const res = await api.get(`/public/tenant-by-domain/${detectedCustomDomain}`);
          const tenant = res.data;
          setSiteSlug(tenant.site_slug);  // Store for popup/navigation use
          
          // Check if this is an article route
          const path = window.location.pathname;
          const articleMatch = path.match(/^\/article\/(.+)$/);
          console.log('[SubdomainRouter] Path:', path, 'Article match:', articleMatch);
          
          if (articleMatch) {
            // Article route - fetch and display the article
            const articleSlug = articleMatch[1];
            console.log('[SubdomainRouter] Article route detected, slug:', articleSlug);
            try {
              const articleRes = await api.get(`/public/site/${tenant.site_slug}/article/${articleSlug}`);
              console.log('[SubdomainRouter] Article API response:', articleRes.data);
              const articlePageData = {
                ...articleRes.data,
                isArticle: true,
                tenant_name: tenant.name
              };
              console.log('[SubdomainRouter] Setting pageData with isArticle:', articlePageData);
              setPageData(articlePageData);
              setTenantData({ tenant_name: tenant.name, theme: articleRes.data.theme });
            } catch (artErr) {
              console.error('[SubdomainRouter] Article fetch error:', artErr);
              setError('Article not found');
            }
          } else if (detectedPageSlug === 'landing') {
            // "/landing" explicitly requested - show Landing Page
            const landingRes = await api.get(`/public/tenant/${tenant.site_slug}`);
            setTenantData(landingRes.data);
            setShowLandingPage(true);
          } else if (detectedPageSlug) {
            // Fetch specific page using site_slug from the tenant (supports hierarchical paths)
            const pageRes = await api.get(`/public/tenant/${tenant.site_slug}/page/${detectedPageSlug}`);
            
            // Handle redirect response
            if (pageRes.data.redirect) {
              console.log('[SubdomainRouter] Redirect detected, navigating to:', pageRes.data.target_path);
              window.location.href = pageRes.data.target_path;
              return;
            }
            
            setPageData(pageRes.data);
            setTenantData({ tenant_name: pageRes.data.tenant_name, theme: pageRes.data.theme });
          } else {
            // Root URL "/" - try to load homepage (isHomepage=true or slug="home")
            try {
              const homePageRes = await api.get(`/public/tenant/${tenant.site_slug}/homepage`);
              setPageData(homePageRes.data);
              setTenantData({ tenant_name: homePageRes.data.tenant_name, theme: homePageRes.data.theme });
            } catch (homeErr) {
              // No homepage found - fall back to Landing Page
              console.log('No homepage found, falling back to Landing Page');
              const landingRes = await api.get(`/public/tenant/${tenant.site_slug}`);
              setTenantData(landingRes.data);
              setShowLandingPage(true);
            }
          }
        } catch (err) {
          console.error('Failed to load tenant by custom domain:', err);
          if (err.response?.status === 404) {
            setError(detectedPageSlug ? 'Page not found' : 'Site not found');
          } else {
            setError('Failed to load site');
          }
        } finally {
          setLoading(false);
        }
        return;
      }
      
      // Then check for subdomain
      const detectedSubdomain = extractSubdomain();
      
      if (!detectedSubdomain) {
        // No subdomain or custom domain - render main app
        setLoading(false);
        return;
      }
      
      setSubdomain(detectedSubdomain);
      const detectedPageSlug = extractPageSlug();
      setPageSlug(detectedPageSlug);
      
      try {
        // Check if this is an article route
        const path = window.location.pathname;
        const articleMatch = path.match(/^\/article\/(.+)$/);
        
        if (articleMatch) {
          // Article route - fetch and display the article
          const articleSlug = articleMatch[1];
          console.log('[SubdomainRouter] Subdomain article route detected, slug:', articleSlug);
          try {
            const articleRes = await api.get(`/public/site/${detectedSubdomain}/article/${articleSlug}`);
            const articlePageData = {
              ...articleRes.data,
              isArticle: true
            };
            setPageData(articlePageData);
            setTenantData({ tenant_name: articleRes.data.tenant_name, theme: articleRes.data.theme });
          } catch (artErr) {
            console.error('[SubdomainRouter] Article fetch error:', artErr);
            setError('Article not found');
          }
        } else if (detectedPageSlug === 'landing') {
          // "/landing" explicitly requested - show Landing Page
          const res = await api.get(`/public/tenant/${detectedSubdomain}`);
          setTenantData(res.data);
          setShowLandingPage(true);
        } else if (detectedPageSlug) {
          // Fetch specific page (supports hierarchical paths like "about/team")
          const res = await api.get(`/public/tenant/${detectedSubdomain}/page/${detectedPageSlug}`);
          
          // Handle redirect response
          if (res.data.redirect) {
            console.log('[SubdomainRouter] Redirect detected, navigating to:', res.data.target_path);
            window.location.href = res.data.target_path;
            return;
          }
          
          setPageData(res.data);
          setTenantData({ tenant_name: res.data.tenant_name, theme: res.data.theme });
        } else {
          // Root URL "/" - try to load homepage (isHomepage=true or slug="home")
          try {
            const homePageRes = await api.get(`/public/tenant/${detectedSubdomain}/homepage`);
            setPageData(homePageRes.data);
            setTenantData({ tenant_name: homePageRes.data.tenant_name, theme: homePageRes.data.theme });
          } catch (homeErr) {
            // No homepage found - fall back to Landing Page
            console.log('No homepage found, falling back to Landing Page');
            const res = await api.get(`/public/tenant/${detectedSubdomain}`);
            setTenantData(res.data);
            setShowLandingPage(true);
          }
        }
      } catch (err) {
        console.error('Failed to load tenant:', err);
        if (err.response?.status === 404) {
          setError(detectedPageSlug ? 'Page not found' : 'Site not found');
        } else {
          setError('Failed to load site');
        }
      } finally {
        setLoading(false);
      }
    };

    checkSubdomainOrDomain();
  }, []);

  // Show region selector for root domain (nexuscms.ai / www.nexuscms.ai)
  if (showRegionSelector) {
    return <RegionSelector />;
  }

  // Loading state — intentionally no <Helmet> so the static "Nexus CMS"
  // title from index.html stays visible during the initial fetch.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Helmet><title>{error === 'Page not found' ? 'Page not found' : 'Site not found'} | Nexus CMS</title></Helmet>        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">404</h1>
          <p className="text-xl text-slate-600 mb-2">{error}</p>
          <p className="text-slate-500 mb-6">
            {error === 'Page not found' 
              ? "The page you're looking for doesn't exist or hasn't been published."
              : "The site you're looking for doesn't exist or has been removed."
            }
          </p>
          {subdomain && (
            <a 
              href="/"
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 mr-3"
            >
              Back to Home
            </a>
          )}
          <a 
            href={`https://${BASE_DOMAIN}`}
            className="inline-flex items-center px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
          >
            Go to NexusCMS
          </a>
        </div>
      </div>
    );
  }

  // Custom domain or subdomain detected with page data - render the page
  // This handles both explicit page slugs AND the "home" page at root URL
  if ((subdomain || customDomain) && pageData && !showLandingPage) {
    return (
      <>
        <PublicTenantPage 
          pageData={pageData}
          subdomain={siteSlug || subdomain}
        />
        <TenantCustomCode subdomain={subdomain} domain={customDomain} />
      </>
    );
  }

  // Show Landing Page when:
  // 1. Explicitly requested via /home URL
  // 2. Fallback when no "home" page exists in Page Builder
  if ((subdomain || customDomain) && tenantData && showLandingPage) {
    return (
      <>
        <PublicTenantLandingPage 
          tenantData={tenantData}
          subdomain={siteSlug || subdomain}
          isSubdomainMode={true}
        />
        <TenantCustomCode subdomain={subdomain} domain={customDomain} />
      </>
    );
  }

  // No subdomain or custom domain - render main app
  return children;
};

export default SubdomainRouter;
